import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: "tour_admin" | "tour_manager";
}

export default function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
        return;
      }
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate("/auth");
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!session) return;

    if (!requireRole) {
      setHasAccess(true);
      setChecking(false);
      return;
    }

    const checkRole = async () => {
      try {
        const { data: hasRole, error } = await supabase.rpc("has_role", {
          _user_id: session.user.id,
          _role: requireRole,
        });

        if (error) throw error;

        if (!hasRole) {
          navigate("/auth");
          return;
        }

        setHasAccess(true);
      } catch (err) {
        console.error("Role check failed:", err);
        navigate("/auth");
      } finally {
        setChecking(false);
      }
    };

    checkRole();
  }, [session, requireRole, navigate]);

  if (checking) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "hsl(0 0% 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, border: "3px solid #3BBFBF", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!hasAccess) return null;

  return <>{children}</>;
}
